/**
 * Statistical calculation utilities and AG Grid comparators.
 * Used for calculating summary statistics and custom sorting behavior.
 */

/**
 * Calculate the arithmetic mean of an array of numbers
 * @param {number[]} data - Array of numeric values
 * @returns {number|null} Mean value or null if empty/invalid
 */
export const calculateMean = (data) => {
  if (!data || data.length === 0) return null;
  const validData = data.filter(v => v !== null && v !== undefined && !isNaN(v));
  if (validData.length === 0) return null;
  const sum = validData.reduce((acc, value) => acc + value, 0);
  return sum / validData.length;
};

/**
 * Calculate the median of an array of numbers
 * @param {number[]} data - Array of numeric values
 * @returns {number|null} Median value or null if empty/invalid
 */
export const calculateMedian = (data) => {
  if (!data || data.length === 0) return null;
  const validData = data.filter(v => v !== null && v !== undefined && !isNaN(v));
  if (validData.length === 0) return null;

  const sortedData = [...validData].sort((a, b) => a - b);
  const middle = Math.floor(sortedData.length / 2);

  return sortedData.length % 2 === 0
    ? (sortedData[middle - 1] + sortedData[middle]) / 2
    : sortedData[middle];
};

/**
 * Calculate summary statistics for a dataset
 * @param {number[]} data - Array of numeric values
 * @returns {Object} Object containing mean, median, min, max, count
 */
export const calculateSummaryStats = (data) => {
  const validData = (data || []).filter(v => v !== null && v !== undefined && !isNaN(v));

  if (validData.length === 0) {
    return { mean: null, median: null, min: null, max: null, count: 0 };
  }

  return {
    mean: calculateMean(validData),
    median: calculateMedian(validData),
    min: Math.min(...validData),
    max: Math.max(...validData),
    count: validData.length,
  };
};

/**
 * Calculate statistics for multiple fields from row data
 * @param {Object[]} rows - Array of row data objects
 * @param {string[]} fields - Array of field names to calculate stats for
 * @returns {Object} Object with mean and median objects keyed by field name
 */
export const calculateFieldStatistics = (rows, fields) => {
  const calculatedMean = {};
  const calculatedMedian = {};

  fields.forEach((field) => {
    const validData = rows
      .map(row => parseFloat(row[field]))
      .filter(value => !isNaN(value));

    calculatedMean[field] = calculateMean(validData);
    calculatedMedian[field] = calculateMedian(validData);
  });

  return { mean: calculatedMean, median: calculatedMedian };
};

/**
 * Create pinned bottom row data for AG Grid with Mean and Median rows
 * @param {Object[]} rows - Array of row data objects
 * @param {string[]} numericalFields - Array of field names to calculate
 * @param {string} labelField - Field name for the row label (default: "Company Name")
 * @returns {Object[]} Array with Mean and Median row objects
 */
export const createPinnedBottomRowData = (rows, numericalFields, labelField = "Company Name") => {
  const { mean, median } = calculateFieldStatistics(rows, numericalFields);

  return [
    { [labelField]: "Mean", ...mean },
    { [labelField]: "Median", ...median },
  ];
};

// ============================================
// AG Grid Comparators
// ============================================

/**
 * Numerical comparator for AG Grid that keeps Mean/Median rows at bottom
 * @param {number} valueA - First value to compare
 * @param {number} valueB - Second value to compare
 * @param {Object} nodeA - First AG Grid row node
 * @param {Object} nodeB - Second AG Grid row node
 * @param {boolean} isInverted - Whether sort is descending
 * @returns {number} Comparison result (-1, 0, or 1)
 */
export const numericalComparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
  const isAStatic = nodeA?.data?.["Company Name"] === "Mean" || nodeA?.data?.["Company Name"] === "Median";
  const isBStatic = nodeB?.data?.["Company Name"] === "Mean" || nodeB?.data?.["Company Name"] === "Median";

  // Keep Mean and Median at bottom regardless of sort direction
  if (isAStatic && isBStatic) return 0;
  if (isAStatic) return isInverted ? -1 : 1;
  if (isBStatic) return isInverted ? 1 : -1;

  // Handle null/undefined values
  if ((valueA === null || valueA === undefined) && (valueB === null || valueB === undefined)) return 0;
  if (valueA === null || valueA === undefined) return isInverted ? -1 : 1;
  if (valueB === null || valueB === undefined) return isInverted ? 1 : -1;

  return valueA - valueB;
};

/**
 * Creates a numerical comparator with custom static row label field
 * @param {string} labelField - Field name that contains row labels
 * @param {string[]} staticLabels - Labels to keep at bottom (default: ["Mean", "Median"])
 * @returns {function} Comparator function for AG Grid
 */
export const createNumericalComparator = (labelField = "Company Name", staticLabels = ["Mean", "Median"]) => {
  return (valueA, valueB, nodeA, nodeB, isInverted) => {
    const isAStatic = staticLabels.includes(nodeA?.data?.[labelField]);
    const isBStatic = staticLabels.includes(nodeB?.data?.[labelField]);

    if (isAStatic && isBStatic) return 0;
    if (isAStatic) return isInverted ? -1 : 1;
    if (isBStatic) return isInverted ? 1 : -1;

    if ((valueA === null || valueA === undefined) && (valueB === null || valueB === undefined)) return 0;
    if (valueA === null || valueA === undefined) return isInverted ? -1 : 1;
    if (valueB === null || valueB === undefined) return isInverted ? 1 : -1;

    return valueA - valueB;
  };
};

/**
 * Date comparator for AG Grid filtering
 * @param {Date|string} filterDate - Filter date value
 * @param {Date|string} cellValue - Cell date value
 * @returns {number} Comparison result (-1, 0, or 1)
 */
export const dateComparator = (filterDate, cellValue) => {
  if (!filterDate || !cellValue) return 0;

  const filterDateObj = new Date(filterDate);
  const cellDateObj = new Date(cellValue);

  if (cellDateObj < filterDateObj) return -1;
  if (cellDateObj > filterDateObj) return 1;
  return 0;
};

/**
 * Date sort comparator for AG Grid column sorting
 * @param {string} valueA - First cell value
 * @param {string} valueB - Second cell value
 * @returns {number} Comparison result (-1, 0, or 1)
 */
export const dateSortComparator = (valueA, valueB) => {
  if (!valueA && !valueB) return 0;
  if (!valueA) return -1;
  if (!valueB) return 1;

  const dateA = new Date(valueA);
  const dateB = new Date(valueB);

  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;
  return 0;
};

// ============================================
// Linear Regression
// ============================================

/**
 * Calculate linear regression using least squares method
 * @param {Array<{x: number, y: number}>} points - Array of data points
 * @returns {Object|null} Regression result { slope, intercept, rSquared, n } or null if insufficient data
 */
export const calculateLinearRegression = (points) => {
  // Filter valid points
  const validPoints = (points || []).filter(
    p => p && typeof p.x === 'number' && typeof p.y === 'number' && !isNaN(p.x) && !isNaN(p.y)
  );

  const n = validPoints.length;
  if (n < 2) return null;

  // Calculate sums
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;
  for (const p of validPoints) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumX2 += p.x * p.x;
    sumY2 += p.y * p.y;
  }

  // Calculate slope and intercept
  const denominator = n * sumX2 - sumX * sumX;
  if (denominator === 0) return null; // All x values are identical

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  // Calculate R-squared
  const meanY = sumY / n;
  let ssTotal = 0, ssResidual = 0;
  for (const p of validPoints) {
    const predicted = slope * p.x + intercept;
    ssTotal += (p.y - meanY) ** 2;
    ssResidual += (p.y - predicted) ** 2;
  }

  const rSquared = ssTotal === 0 ? 0 : 1 - (ssResidual / ssTotal);

  return { slope, intercept, rSquared, n };
};

/**
 * Get line endpoints for rendering regression line
 * @param {Object} regression - Regression result from calculateLinearRegression
 * @param {number} xMin - Minimum x value for line
 * @param {number} xMax - Maximum x value for line
 * @returns {Object|null} Line points { x1, y1, x2, y2 } or null if invalid
 */
export const getRegressionLinePoints = (regression, xMin, xMax) => {
  if (!regression || typeof regression.slope !== 'number') return null;

  const { slope, intercept } = regression;
  const y1 = slope * xMin + intercept;
  const y2 = slope * xMax + intercept;

  return { x1: xMin, y1, x2: xMax, y2 };
};

// ============================================
// Correlation Analysis
// ============================================

/**
 * Calculate Pearson correlation coefficient between two arrays
 * @param {number[]} x - First array of values
 * @param {number[]} y - Second array of values
 * @returns {number|null} Correlation coefficient (-1 to 1) or null if insufficient data
 */
export const calculatePearsonCorrelation = (x, y) => {
  if (!x || !y || x.length !== y.length || x.length < 2) return null;

  // Filter pairs where both values are valid
  const pairs = [];
  for (let i = 0; i < x.length; i++) {
    const xi = x[i];
    const yi = y[i];
    if (xi !== null && xi !== undefined && !isNaN(xi) &&
        yi !== null && yi !== undefined && !isNaN(yi)) {
      pairs.push({ x: xi, y: yi });
    }
  }

  const n = pairs.length;
  if (n < 2) return null;

  // Calculate means
  let sumX = 0, sumY = 0;
  for (const p of pairs) {
    sumX += p.x;
    sumY += p.y;
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  // Calculate correlation
  let numerator = 0;
  let sumSqX = 0;
  let sumSqY = 0;

  for (const p of pairs) {
    const dx = p.x - meanX;
    const dy = p.y - meanY;
    numerator += dx * dy;
    sumSqX += dx * dx;
    sumSqY += dy * dy;
  }

  const denominator = Math.sqrt(sumSqX * sumSqY);
  if (denominator === 0) return null;

  return numerator / denominator;
};

/**
 * Calculate correlation matrix for multiple metrics
 * @param {Object[]} data - Array of data objects
 * @param {Object[]} metrics - Array of { field, label } metric definitions
 * @returns {Object} { matrix: number[][], labels: string[], fields: string[] }
 */
export const calculateCorrelationMatrix = (data, metrics) => {
  if (!data || !metrics || data.length < 2 || metrics.length < 2) {
    return { matrix: [], labels: [], fields: [] };
  }

  const n = metrics.length;
  const labels = metrics.map(m => m.label || m.field);
  const fields = metrics.map(m => m.field);

  // Extract column values for each metric
  const columns = fields.map(field =>
    data.map(row => {
      const value = row[field];
      return (value !== null && value !== undefined && !isNaN(value)) ? value : null;
    })
  );

  // Calculate correlation matrix
  const matrix = [];
  for (let i = 0; i < n; i++) {
    const row = [];
    for (let j = 0; j < n; j++) {
      if (i === j) {
        row.push(1); // Perfect self-correlation
      } else if (j < i) {
        row.push(matrix[j][i]); // Symmetric matrix
      } else {
        const corr = calculatePearsonCorrelation(columns[i], columns[j]);
        row.push(corr);
      }
    }
    matrix.push(row);
  }

  return { matrix, labels, fields };
};

/**
 * Transform correlation matrix to Nivo HeatMap format
 * @param {Object} correlationResult - Result from calculateCorrelationMatrix
 * @returns {Object[]} Array of objects for Nivo HeatMap
 */
export const correlationMatrixToHeatmapData = (correlationResult) => {
  const { matrix, labels } = correlationResult;
  if (!matrix || matrix.length === 0) return [];

  return matrix.map((row, rowIndex) => {
    const dataPoint = { id: labels[rowIndex] };
    row.forEach((value, colIndex) => {
      dataPoint[labels[colIndex]] = value;
    });
    return dataPoint;
  });
};

// ============================================
// Multi-Condition Outlier Detection
// ============================================

/**
 * Detect outliers using multi-condition IQR method matching calculateScatterRange logic.
 * Upper outlier requires ALL three: exceeds Q3 + 2.5*IQR, is >2.5x median, is >1.5x P90.
 * Lower outlier: below Q1 - 2.5*IQR AND negative.
 * Falls back to all-inlier if fewer than 4 data points.
 * @param {number[]} values - 1D array of numbers
 * @returns {Object} { outlierIndices: Set<number>, inlierIndices: Set<number> }
 */
export const detectMultiConditionOutliers = (values) => {
  const validValues = (values || []).filter(v => v !== null && v !== undefined && !isNaN(v));
  const n = validValues.length;

  // Not enough data for IQR — treat all as inliers
  if (n < 4) {
    return {
      outlierIndices: new Set(),
      inlierIndices: new Set(validValues.map((_, i) => i)),
    };
  }

  const sorted = [...validValues].sort((a, b) => a - b);
  const midIndex = Math.floor(n / 2);

  const lowerHalf = sorted.slice(0, midIndex);
  const upperHalf = n % 2 === 0 ? sorted.slice(midIndex) : sorted.slice(midIndex + 1);

  const q1 = lowerHalf[Math.floor(lowerHalf.length / 2)];
  const q3 = upperHalf[Math.floor(upperHalf.length / 2)];
  const median = sorted[midIndex];

  const iqr = q3 - q1;
  const lowerBound = q1 - 2.5 * iqr;
  const upperBound = q3 + 2.5 * iqr;

  const p90Index = Math.floor(n * 0.9);
  const p90 = sorted[p90Index];

  const outlierIndices = new Set();
  const inlierIndices = new Set();

  validValues.forEach((v, i) => {
    const isExtremeVsMedian = median > 0 && v > median * 2.5;
    const isExtremeVsP90 = p90 > 0 && v > p90 * 1.5;

    const isUpperOutlier = v > upperBound && isExtremeVsMedian && isExtremeVsP90;
    const isLowerOutlier = v < lowerBound && v < 0;

    if (isUpperOutlier || isLowerOutlier) {
      outlierIndices.add(i);
    } else {
      inlierIndices.add(i);
    }
  });

  return { outlierIndices, inlierIndices };
};

// ============================================
// Quartile and Outlier Detection
// ============================================

/**
 * Calculate quartiles (Q1, Q2/median, Q3) for an array of numbers
 * @param {number[]} data - Array of numeric values
 * @returns {Object|null} { q1, q2, q3 } or null if insufficient data
 */
export const calculateQuartiles = (data) => {
  const validData = (data || []).filter(v => v !== null && v !== undefined && !isNaN(v));
  if (validData.length < 4) return null;

  const sorted = [...validData].sort((a, b) => a - b);
  const n = sorted.length;

  // Q2 (median)
  const q2 = calculateMedian(sorted);

  // For Q1 and Q3, use the median of lower/upper halves
  const midIndex = Math.floor(n / 2);
  const lowerHalf = sorted.slice(0, midIndex);
  const upperHalf = n % 2 === 0 ? sorted.slice(midIndex) : sorted.slice(midIndex + 1);

  const q1 = calculateMedian(lowerHalf);
  const q3 = calculateMedian(upperHalf);

  return { q1, q2, q3 };
};

/**
 * Detect outliers using IQR (Interquartile Range) method
 * @param {number[]} data - Array of numeric values
 * @param {number} iqrMultiplier - Multiplier for IQR (default: 3.0 for extreme outliers only)
 * @returns {Object} { inliers, outliers, bounds: { lower, upper }, iqr }
 */
export const detectOutliers = (data, iqrMultiplier = 3.0) => {
  const validData = (data || []).filter(v => v !== null && v !== undefined && !isNaN(v));

  if (validData.length < 4) {
    return {
      inliers: validData,
      outliers: [],
      bounds: { lower: Math.min(...validData), upper: Math.max(...validData) },
      iqr: 0,
    };
  }

  const quartiles = calculateQuartiles(validData);
  if (!quartiles) {
    return {
      inliers: validData,
      outliers: [],
      bounds: { lower: Math.min(...validData), upper: Math.max(...validData) },
      iqr: 0,
    };
  }

  const { q1, q3 } = quartiles;
  const iqr = q3 - q1;
  const lowerBound = q1 - iqrMultiplier * iqr;
  const upperBound = q3 + iqrMultiplier * iqr;

  const inliers = [];
  const outliers = [];

  validData.forEach((value) => {
    if (value < lowerBound || value > upperBound) {
      outliers.push(value);
    } else {
      inliers.push(value);
    }
  });

  return {
    inliers,
    outliers,
    bounds: { lower: lowerBound, upper: upperBound },
    iqr,
  };
};

/**
 * Calculate optimal scatter plot bounds with outlier handling
 * @param {Array<{x: number, y: number}>} points - Array of data points
 * @param {Object} options - Configuration options
 * @param {number} options.iqrMultiplier - IQR multiplier for outlier detection (default: 3.0 for extreme only)
 * @param {number} options.padding - Percentage padding to add to bounds (default: 0.1 = 10%)
 * @param {number} options.minRange - Minimum range to enforce (prevents tiny ranges)
 * @returns {Object} { x: { min, max, hasOutliers }, y: { min, max, hasOutliers }, outlierPoints }
 */
export const calculateScatterBounds = (points, options = {}) => {
  const {
    iqrMultiplier = 3.0, // More tolerant - only extreme outliers
    padding = 0.1,
    minRange = 10,
  } = options;

  const validPoints = (points || []).filter(
    p => p && typeof p.x === 'number' && typeof p.y === 'number' && !isNaN(p.x) && !isNaN(p.y)
  );

  if (validPoints.length === 0) {
    return {
      x: { min: 0, max: 100, hasOutliers: false },
      y: { min: 0, max: 100, hasOutliers: false },
      outlierPoints: [],
    };
  }

  if (validPoints.length < 4) {
    // Not enough data for IQR - use simple min/max with padding
    const xValues = validPoints.map(p => p.x);
    const yValues = validPoints.map(p => p.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    const xRange = Math.max(xMax - xMin, minRange);
    const yRange = Math.max(yMax - yMin, minRange);

    return {
      x: {
        min: Math.max(0, xMin - xRange * padding),
        max: xMax + xRange * padding,
        hasOutliers: false,
      },
      y: {
        min: Math.max(0, yMin - yRange * padding),
        max: yMax + yRange * padding,
        hasOutliers: false,
      },
      outlierPoints: [],
    };
  }

  // Extract x and y values
  const xValues = validPoints.map(p => p.x);
  const yValues = validPoints.map(p => p.y);

  // Detect outliers for each axis
  const xOutlierResult = detectOutliers(xValues, iqrMultiplier);
  const yOutlierResult = detectOutliers(yValues, iqrMultiplier);

  // Calculate bounds based on inliers
  const xInlierMin = xOutlierResult.inliers.length > 0 ? Math.min(...xOutlierResult.inliers) : 0;
  const xInlierMax = xOutlierResult.inliers.length > 0 ? Math.max(...xOutlierResult.inliers) : 100;
  const yInlierMin = yOutlierResult.inliers.length > 0 ? Math.min(...yOutlierResult.inliers) : 0;
  const yInlierMax = yOutlierResult.inliers.length > 0 ? Math.max(...yOutlierResult.inliers) : 100;

  // Apply padding
  const xRange = Math.max(xInlierMax - xInlierMin, minRange);
  const yRange = Math.max(yInlierMax - yInlierMin, minRange);

  const xBounds = {
    min: Math.max(0, xInlierMin - xRange * padding),
    max: xInlierMax + xRange * padding,
    hasOutliers: xOutlierResult.outliers.length > 0,
  };

  const yBounds = {
    min: Math.max(0, yInlierMin - yRange * padding),
    max: yInlierMax + yRange * padding,
    hasOutliers: yOutlierResult.outliers.length > 0,
  };

  // Identify which points are outliers
  const outlierPoints = validPoints.filter((p) => {
    const isXOutlier = p.x < xOutlierResult.bounds.lower || p.x > xOutlierResult.bounds.upper;
    const isYOutlier = p.y < yOutlierResult.bounds.lower || p.y > yOutlierResult.bounds.upper;
    return isXOutlier || isYOutlier;
  });

  return {
    x: xBounds,
    y: yBounds,
    outlierPoints,
  };
};
